import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { Copy } from "lucide-react";
import { FloatingGhost } from "./Ghost";
import { copyCA, CA_SHORT } from "../lib/ca";

const HERO_IMG =
  "https://images.unsplash.com/photo-1572860177022-8fda92a90c95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxzcG9va3klMjBoYWxsb3dlZW4lMjBwdW1wa2luJTIwZ2xvd2luZ3xlbnwwfHx8fDE3OTE4OTcxNDd8MA&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "WHEN FOMO", accent: false },
  { text: "STRIKES ON", accent: false },
  { text: "ALL HALLOWS EVE", accent: true },
];

const stats = [
  { value: "666M", label: "Max Haunted Supply" },
  { value: "0 / 0", label: "Buy · Sell Tax" },
  { value: "100%", label: "LP Burned at Genesis" },
  { value: "OCT 31", label: "Moon Mission" },
];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 16 });
  const sy = useSpring(my, { stiffness: 60, damping: 16 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-20, 20]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-16, 16]);
  const ghostDriftX = useTransform(sx, [-0.5, 0.5], [40, -40]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pb-16 pt-32 sm:px-12 lg:px-20"
    >
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-phantom/20 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[30rem] w-[30rem] rounded-full bg-pumpkin/15 blur-[140px]" />

      <FloatingGhost className="left-[6%] top-[22%] hidden md:block" size={52} opacity={0.5} />
      <FloatingGhost className="right-[10%] top-[16%] hidden lg:block" size={68} delay={1.2} opacity={0.65} />
      <motion.div style={{ x: ghostDriftX }} className="absolute bottom-[18%] left-[42%] hidden xl:block">
        <FloatingGhost className="relative" size={44} delay={2} opacity={0.4} />
      </motion.div>

      <motion.div
        style={{ opacity: contentFade }}
        className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 lg:grid-cols-[1.15fr_1fr]"
      >
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-orange-400/80 sm:text-xs"
          >
            The ticker that haunts the charts {"//"}{" "}
            <span className="text-pumpkin">$FOMOWEEN</span>
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-cinzel text-4xl font-black uppercase leading-[1.08] tracking-tight text-zinc-100 sm:text-5xl lg:text-6xl"
          >
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${
                    line.accent
                      ? "text-pumpkin drop-shadow-[0_0_28px_rgba(255,107,0,0.5)]"
                      : ""
                  }`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.35 + i * 0.18,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400"
          >
            Built for degens who fear missing out more than the underworld
            itself. The official Halloween memecoin conjured for the FOMO app.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => onNavigate("#community")}
              className="animate-pulse-glow rounded-full bg-pumpkin px-8 py-4 font-syne text-sm font-bold uppercase tracking-widest text-abyss transition-transform duration-300 hover:-translate-y-1"
            >
              Join the Coven
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => onNavigate("#tokenomics")}
              className="rounded-full border border-purple-500/40 bg-elevated/60 px-8 py-4 font-syne text-sm font-bold uppercase tracking-widest text-purple-200 transition-colors duration-300 hover:border-purple-400/80 hover:text-white"
            >
              Read Contract Lore
            </button>
            <button
              data-testid="hero-ca-pill"
              onClick={copyCA}
              className="flex items-center gap-2 rounded-full border border-dashed border-orange-500/40 px-5 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-orange-300/90 transition-colors hover:border-orange-400/80 hover:text-orange-200"
            >
              <Copy size={13} /> CA: {CA_SHORT}
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: parallaxY }} className="relative mx-auto w-full max-w-md">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
            className="relative"
          >
            <motion.div
              style={{ x: imgX, y: imgY }}
              initial={{ opacity: 0, scale: 0.92, rotate: 4 }}
              animate={{ opacity: 1, scale: 1, rotate: 2 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-[2rem] border border-orange-500/30 shadow-[0_0_60px_rgba(255,107,0,0.25)]"
            >
              <img
                src={HERO_IMG}
                alt="Glowing jack-o'-lantern wrapped in eerie smoke"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-abyss/90 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-xl border border-orange-500/25 bg-abyss/70 px-4 py-3 backdrop-blur-md">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-orange-300">
                  Jack-o'-Liquidity
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  Est. Oct 31
                </span>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        data-testid="hero-stats-bar"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 mx-auto mt-20 grid w-full max-w-7xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-orange-500/20 bg-orange-500/10 lg:grid-cols-4"
      >
        {stats.map((s) => (
          <div key={s.label} className="bg-[#0B0814]/90 p-6">
            <p className="font-cinzel text-2xl font-black text-pumpkin sm:text-3xl">
              {s.value}
            </p>
            <p className="mt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">
              {s.label}
            </p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
